import { Col, DatePicker, Form, Row } from 'antd'
import FormlessModal from 'components/CIMSModal/FormlessModal'
import { CIMSModalWrapperProps } from 'components/CIMSModal/types'
import { simpleSuccessNotification } from 'hooks/notificationHandler'
import {
  ExternalLinkInput,
  useDraftRequestForInformationMutation,
  useRemoveSharePointResourceByFolderMutation,
  useSubmitRequestForInformationMutation,
} from 'schema'
import { DefaultMutationHookOptions } from 'utils/defaults'
import { useForm, useWatch } from 'antd/lib/form/Form'
import CIMSFormItem from 'components/CIMSFormItem'
import CIMSSelect from 'components/CIMSSelect'
import { useRFITrackerContext } from '../../../../RFITrackerContext'
import { Response } from '../../../../RFITrackerContext/types'
import { useEffect, useState } from 'react'
import SharePointUploader from 'components/SharePointUploader'
import { SharePointParentFolder } from '@cims-online/shared'
import ExternalLinkEditor from 'components/ExternalLinkEditor'
import { Moment } from 'moment'
import CustomFieldEditor from 'components/CustomFields/CustomFieldEditor'
import { CustomFieldEditorValues } from 'components/CustomFields/CustomFieldEditor/types'
import { useProjectCustomFieldsContext } from 'components/CustomFields/ProjectCustomFieldsContext'
import { HelpMessage } from 'utils/constants'
import { useRespondedResponseDisciplineOptions } from '../../hooks/useRespondedResponseDisciplineOptions'
import CIMSTextArea from 'components/CIMSTextArea'
import { useProjectContext } from 'context/ProjectContext'

const Help = HelpMessage.RequestsForInformation.RFITracker

export type DraftSubmitRFIModalData = Partial<
  Omit<DraftSubmitRFIModalValues, 'number'>
> & {
  id?: string
  number?: number
}

type DraftSubmitRFIModalValues = {
  description: string
  responseDisciplineIds?: string[]
  responses?: Response[]
  organizationId?: string | null
  requestedResponseDate?: Moment | null
  externalLinks?: ExternalLinkInput[]
  number: string
} & Partial<CustomFieldEditorValues>

type SubmitRFIInputValues = Required<
  Pick<
    DraftSubmitRFIModalValues,
    'description' | 'responseDisciplineIds' | 'responses' | 'externalLinks'
  >
> & { organizationId: string }

const DraftSubmitRFIModal: React.FC<
  CIMSModalWrapperProps<DraftSubmitRFIModalData>
> = ({ visible, onClose, state }) => {
  const { projectId } = useProjectContext()
  const {
    refetch,
    metadata,
    selectOptions: { organizationOptions },
  } = useRFITrackerContext()
  const { toMutateCustomFieldsInput } = useProjectCustomFieldsContext()
  const [form] = useForm<DraftSubmitRFIModalValues>()
  const number = useWatch('number', form)
  const descriptionWatch = useWatch('description', form)
  const responseDisciplineIdsWatch = useWatch('responseDisciplineIds', form)
  const organizationIdWatch = useWatch('organizationId', form)
  const requestedResponseDateWatch = useWatch('requestedResponseDate', form)
  const [canSubmit, setCanSubmit] = useState<boolean>(false)
  const responseDisciplineOptions = useRespondedResponseDisciplineOptions({
    rfiResponses: state?.responses ?? [],
    allResponseDisciplines: metadata?.rfiResponseDisciplines,
  })
  const [draftRFI, { loading: drafting }] =
    useDraftRequestForInformationMutation({
      ...DefaultMutationHookOptions,
      onCompleted: ({
        draftRequestForInformation: {
          requestForInformation: { formattedNumber },
        },
      }) =>
        simpleSuccessNotification(`${formattedNumber} drafted successfully!`),
    })
  const [submitRFI, { loading: submitting }] =
    useSubmitRequestForInformationMutation({
      ...DefaultMutationHookOptions,
      onCompleted: ({
        submitRequestForInformation: {
          requestForInformation: { formattedNumber },
        },
      }) =>
        simpleSuccessNotification(`${formattedNumber} submitted successfully!`),
    })
  const [removeSharePointResource] =
    useRemoveSharePointResourceByFolderMutation({
      ...DefaultMutationHookOptions,
      onCompleted: ({ removeSharePointResource: { message, removed } }) => {
        if (removed) simpleSuccessNotification(message)
      },
    })
  useEffect(() => {
    setCanSubmit(
      !!descriptionWatch &&
        (responseDisciplineIdsWatch?.length ?? 0) > 0 &&
        !!organizationIdWatch &&
        !!requestedResponseDateWatch
    )
  }, [
    descriptionWatch,
    responseDisciplineIdsWatch,
    organizationIdWatch,
    requestedResponseDateWatch,
  ])

  // fields don't reset correctly without this
  useEffect(() => {
    form.setFieldsValue({
      number: state?.number?.toString() ?? crypto.randomUUID(),
      description: state?.description,
      responseDisciplineIds: state?.responseDisciplineIds,
      organizationId: state?.organizationId,
      requestedResponseDate: state?.requestedResponseDate,
      externalLinks: state?.externalLinks,
    })
  }, [
    state,
    form,
    // Reset the form fields every time the modal is opened. This ensures that a
    // unique number is generated for the `number` field when the state is
    // `null`.
    visible,
  ])

  return (
    <FormlessModal
      title={`${canSubmit || state ? 'Submit' : 'Draft'} RFI`}
      visible={visible}
      ok={{
        fn: () => form.submit(),
        title: canSubmit || state ? 'Submit' : 'Draft',
        loading: drafting || submitting,
      }}
      cancel={{
        fn: async () => {
          if (!state) {
            removeSharePointResource({
              variables: {
                removeSharePointResourceInput: {
                  projectId,
                  byFolder: {
                    folder: form.getFieldValue('number'),
                    parentId: SharePointParentFolder.RequestForInformation.id,
                  },
                },
              },
            })
          }
          form.resetFields()
          onClose()
        },
      }}
    >
      <Form
        form={form}
        initialValues={{
          number: state?.number?.toString() ?? crypto.randomUUID(),
          description: state?.description,
          responseDisciplineIds: state?.responseDisciplineIds,
          organizationId: state?.organizationId,
          requestedResponseDate: state?.requestedResponseDate,
          externalLinks: state?.externalLinks,
        }}
        onFinish={async ({
          requestedResponseDate,
          customFields,
          ...rest
        }: DraftSubmitRFIModalValues) => {
          if (canSubmit) {
            await submitRFI({
              variables: {
                submitRequestForInformationInput: {
                  id: state?.id,
                  projectId,
                  requestedResponseDate: requestedResponseDate!.toISOString(),
                  customFields: toMutateCustomFieldsInput(customFields),
                  ...(rest as SubmitRFIInputValues),
                },
              },
            })
          } else {
            await draftRFI({
              variables: {
                draftRequestForInformationInput: {
                  projectId,
                  requestedResponseDate: requestedResponseDate?.toISOString(),
                  customFields: toMutateCustomFieldsInput(customFields),
                  ...rest,
                },
              },
            })
          }
          refetch()
          onClose()
        }}
      >
        <Form.Item hidden name="number" />
        <CIMSFormItem
          name="description"
          label="Description"
          requiredMessage
          help={Help.Description}
        >
          <CIMSTextArea />
        </CIMSFormItem>
        <CIMSFormItem
          name="responseDisciplineIds"
          label="Response Disciplines"
          requiredMessage={
            state ? 'Please select at least one response discipline' : undefined
          }
          help={Help.ResponseDisciplines}
        >
          <CIMSSelect
            simple
            mode="multiple"
            options={responseDisciplineOptions}
          />
        </CIMSFormItem>
        <Row gutter={30}>
          <Col xs={24} xl={12}>
            <CIMSFormItem
              name="organizationId"
              label="Submitting Organization"
              requiredMessage={
                state ? 'Please select an organization' : undefined
              }
              help={Help.Organization}
            >
              <CIMSSelect simple options={organizationOptions} />
            </CIMSFormItem>
          </Col>
          <Col xs={24} xl={12}>
            <CIMSFormItem
              name="requestedResponseDate"
              label="Requested Response Date"
              requiredMessage={
                state ? 'Please pick a requested response date' : undefined
              }
              help={Help.ResponseDate}
            >
              <DatePicker allowClear />
            </CIMSFormItem>
          </Col>
        </Row>
        <Form.Item>
          <SharePointUploader
            parent={SharePointParentFolder.RequestForInformation}
            folder={number}
            sharePointResourceList={false}
            generic={!state}
          />
        </Form.Item>
        <CustomFieldEditor alreadyDisabled={false} hideBooleans={!!state} />
        <CIMSFormItem name="externalLinks">
          <ExternalLinkEditor
            quickLinkOptions={{
              loading: metadata?.loading,
              quickLinks: metadata?.rfiQuickLinks,
            }}
          />
        </CIMSFormItem>
      </Form>
    </FormlessModal>
  )
}

export default DraftSubmitRFIModal
